import React, { useEffect } from 'react';
import classNames from 'classnames';

import ToolbarButton from './ToolbarButton';
import ToolbarPageCounter from './ToolbarPageCounter';
import ToolbarSeparator from './ToolbarSeparator';
import ToolbarSearch from './ToolbarSearch';
import ToolbarButtonGroup from './ToolbarButtonGroup';
import ToolbarColorPicker from './ToolbarColorPicker';
import ToolbarPlaybackRateSelector from './ToolbarPlaybackRateSelector';
import './styles.scss';

const Toolbar = React.forwardRef((props, ref) => {
  const classes = classNames({
    [props.className]: true,
    [`up-toolbar-${props.type}`]: true,
    'up-toolbar': true,
    'up-visible': props.show,
    'up-hidden': !props.show,
    'up-desktop': !props.mobile,
    'up-mobile': props.mobile,
    [props.layout]: !!props.layout
  });

  const buttons = props.traverseButtons
    ? props.traverseButtons(props.children)
    : React.Children.toArray(props.children);
  const buttonRefs = buttons.map(() => React.createRef());
  const canBeActive = button => !button.props.disabled && button.type !== ToolbarSeparator;
  const initialActive = buttons.findIndex(button => canBeActive(button));
  const [active, setActive] = React.useState(initialActive);

  const focusLeft = () => {
    const leftButton = buttons
      .slice(0, active)
      .reverse()
      .find(button => canBeActive(button));
    return leftButton !== undefined ? buttons.indexOf(leftButton) : active;
  };

  const focusRight = () => {
    const rightButton = buttons.slice(active + 1, buttons.length).find(button => canBeActive(button));
    return rightButton !== undefined ? buttons.indexOf(rightButton) : active;
  };

  const onTab = event => {
    if (event.key === 'Tab' && !!buttons[active].props.disabled) {
      // let newActive = event.shiftKey ? focusLeft() : focusRight();
      let newActive = active;
      if (event.shiftKey) {
        newActive = focusLeft();
      } else {
        newActive = focusRight();
      }
      setActive(newActive);
    }
  };

  useEffect(() => {
    window.addEventListener('keydown', onTab);

    return () => {
      window.removeEventListener('keydown', onTab);
    };
  }, [onTab]);

  const onKeyDown = event => {
    !!props.onKeyDown && props.onKeyDown(event);

    let newActive = active;
    switch (event.key) {
      case 'ArrowLeft': {
        newActive = focusLeft();
        break;
      }
      case 'ArrowRight': {
        newActive = focusRight();
        break;
      }
      case 'Home': {
        newActive = 0;
        break;
      }
      case 'End': {
        newActive = buttons.length - 1;
        break;
      }
      default:
        break;
    }

    // keeps submenu open
    // so the submenu items can be accessed
    if (newActive !== active) {
      const ref = buttonRefs[newActive];
      ref.current.focus();
      setActive(newActive);
    }
  };

  const buttonOverride = (child, index) => {
    if (child.type === ToolbarSeparator) {
      return child;
    }

    const additionalProps = {
      ref: buttonRefs[index],
      tabIndex: !props.disabled && active === index ? 0 : -1,
      action: () => {
        !!child.props.action && child.props.action();
        setActive(index);
      }
    };

    return React.cloneElement(child, additionalProps);
  };

  return (
    <div
      aria-label="Universal Player Toolbar"
      datatest={props.datatest || 'toolbar'}
      className={classes}
      onKeyDown={onKeyDown}
      ref={ref}
      role="toolbar"
      style={props.style}
      tabIndex={props.tabIndex}
    >
      {props.traverseButtons ? props.traverseButtons(props.children, buttonOverride) : buttons.map(buttonOverride)}
    </div>
  );
});

export {
  Toolbar,
  ToolbarSeparator,
  ToolbarButton,
  ToolbarPageCounter,
  ToolbarSearch,
  ToolbarButtonGroup,
  ToolbarColorPicker,
  ToolbarPlaybackRateSelector
};
